"use client";

import Link from "@/infrastructure/router/Link";
import type { CSSProperties } from "react";
import {
	ArtistIcon,
	EmptyState,
	GlassCard,
} from "@/components/design-system/ArtistDesignSystem";
import type { LibraryNote } from "./library-data";

export interface NotesListViewProps {
	readonly notes: readonly LibraryNote[];
	readonly onDelete?: ((note: LibraryNote) => void) | undefined;
	readonly onNoteTap?: ((note: LibraryNote) => void) | undefined;
	readonly emptyTitle: string;
	readonly emptyMessage: string;
}

const listStyle: CSSProperties = {
	display: "grid",
	gap: 12,
	padding: "12px 16px",
	listStyle: "none",
};

const plazaEntryStyle: CSSProperties = {
	display: "block",
	padding: "12px 16px 4px",
	textDecoration: "none",
};

const plazaCardStyle: CSSProperties = {
	padding: 14,
	display: "flex",
	alignItems: "center",
	gap: 12,
	width: "100%",
};

const plazaCopyStyle: CSSProperties = {
	display: "grid",
	gap: 2,
	flex: 1,
	minWidth: 0,
};

const plazaTrailingStyle: CSSProperties = {
	color: "var(--ds-text-secondary)",
	display: "inline-flex",
	alignItems: "center",
};

/**
 * NotesListView — vertical list of note cards with quote glyph, visibility
 * badge, body, media grid (1 / 2-3 / 4+ modes), and a timestamp footer.
 * Mirrors `NotesListView` + `NoteCardView` on iOS.
 */
export function NotesListView({
	notes,
	onDelete,
	onNoteTap,
	emptyTitle,
	emptyMessage,
}: Readonly<NotesListViewProps>) {
	if (notes.length === 0) {
		return (
			<EmptyState
				className="library-empty"
				title={emptyTitle}
				message={emptyMessage}
				symbol={<ArtistIcon name="message" size={36} />}
			/>
		);
	}

	return (
		<>
			<Link
				href="/community"
				className="library-notes-plaza-entry"
				style={plazaEntryStyle}
				aria-label="进入笔记广场"
			>
				<GlassCard
					className="library-notes-plaza-card"
					cornerRadius={16}
					style={plazaCardStyle}
				>
					<span
						style={{
							display: "grid",
							placeItems: "center",
							width: 36,
							height: 36,
							borderRadius: "var(--ds-radius-full)",
							background:
								"color-mix(in display-p3, var(--ds-accent) 18%, transparent)",
							color: "var(--ds-accent)",
						}}
						aria-hidden="true"
					>
						<ArtistIcon name="globe" size={18} strokeWidth={1.8} />
					</span>
					<span style={plazaCopyStyle}>
						<strong
							style={{
								fontSize: "0.96rem",
								fontWeight: 650,
								color: "var(--ds-text-primary)",
							}}
						>
							笔记广场
						</strong>
						<small
							style={{ fontSize: "0.78rem", color: "var(--ds-text-secondary)" }}
						>
							查看公开笔记与社区动态
						</small>
					</span>
					<span style={plazaTrailingStyle} aria-hidden="true">
						<ArtistIcon name="chevron" size={18} strokeWidth={1.8} />
					</span>
				</GlassCard>
			</Link>
			<ul className="library-notes-list" style={listStyle}>
				{notes.map((note) => (
					<li key={note.id}>
						<NoteCard note={note} onDelete={onDelete} onTap={onNoteTap} />
					</li>
				))}
			</ul>
		</>
	);
}

function NoteCard({
	note,
	onDelete,
	onTap,
}: Readonly<{
	note: LibraryNote;
	onDelete?: ((note: LibraryNote) => void) | undefined;
	onTap?: ((note: LibraryNote) => void) | undefined;
}>) {
	return (
		<GlassCard
			className="library-note-card"
			cornerRadius={16}
			style={{ padding: 16 }}
		>
			<button
				type="button"
				className="library-note-card__body"
				onClick={() => onTap?.(note)}
				aria-label="查看笔记"
				style={{
					display: "grid",
					gap: 12,
					textAlign: "left",
					width: "100%",
					border: 0,
					padding: 0,
					background: "transparent",
					color: "inherit",
					cursor: "pointer",
					font: "inherit",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<ArtistIcon
						name="message"
						size={24}
						strokeWidth={1.6}
						style={{
							color:
								"color-mix(in display-p3, var(--ds-text-secondary) 35%, transparent)",
						}}
					/>
					<span style={{ flex: 1 }} />
					<VisibilityBadge isLocal={note.isLocal} />
					{note.isLocal && onDelete !== undefined ? (
						<button
							type="button"
							className="library-note-card__delete"
							onClick={(event) => {
								event.stopPropagation();
								onDelete(note);
							}}
							aria-label="删除笔记"
							style={{
								display: "grid",
								placeItems: "center",
								width: 32,
								height: 32,
								border: 0,
								borderRadius: "50%",
								color: "var(--ds-text-secondary)",
								background: "transparent",
								cursor: "pointer",
							}}
						>
							<ArtistIcon name="trash" size={14} strokeWidth={1.8} />
						</button>
					) : null}
				</div>

				<p
					className="library-note-card__content"
					style={{
						margin: 0,
						fontSize: "0.98rem",
						lineHeight: 1.55,
						color: "var(--ds-text-primary)",
						display: "-webkit-box",
						WebkitLineClamp: 5,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					}}
				>
					{note.content}
				</p>

				{note.mediaURLs.length > 0 ? (
					<NoteMediaGrid urls={note.mediaURLs} />
				) : null}

				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
						color: "var(--ds-text-secondary)",
					}}
				>
					<time style={{ fontSize: "0.78rem" }}>{note.timestamp}</time>
					<span style={{ flex: 1 }} />
					{note.mediaURLs.length > 0 ? (
						<span
							style={{
								display: "flex",
								alignItems: "center",
								gap: 4,
								fontSize: "0.78rem",
							}}
						>
							<ArtistIcon name="image" size={12} strokeWidth={1.8} />
							<span>{note.mediaURLs.length}</span>
						</span>
					) : null}
					{note.isLocal ? (
						<ArtistIcon name="shield" size={12} strokeWidth={1.8} />
					) : null}
				</div>
			</button>
		</GlassCard>
	);
}

function VisibilityBadge({ isLocal }: Readonly<{ isLocal: boolean }>) {
	const baseStyle: CSSProperties = {
		padding: "4px 8px",
		borderRadius: "var(--ds-radius-full)",
		fontSize: "0.68rem",
		fontWeight: 700,
	};
	const style: CSSProperties = isLocal
		? {
				...baseStyle,
				color: "var(--ds-text-secondary)",
				background:
					"color-mix(in display-p3, var(--ds-surface) 80%, transparent)",
			}
		: {
				...baseStyle,
				color: "var(--ds-accent)",
				background:
					"color-mix(in display-p3, var(--ds-accent) 18%, transparent)",
			};
	return <span style={style}>{isLocal ? "私有" : "公开"}</span>;
}

function NoteMediaGrid({ urls }: Readonly<{ urls: readonly string[] }>) {
	const count = urls.length;
	const imgStyle: CSSProperties = {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		display: "block",
	};

	if (count === 1) {
		const url = urls[0];
		if (url === undefined) return null;
		return (
			<div
				style={{
					width: "100%",
					height: 150,
					borderRadius: 8,
					overflow: "hidden",
					background: "var(--ds-surface)",
				}}
			>
				{/* biome-ignore lint/performance/noImgElement: note media thumbnails with dynamic inline styling; next/image not feasible on static export */}
				<img src={url} alt="" style={imgStyle} loading="lazy" />
			</div>
		);
	}

	if (count <= 3) {
		return (
			<div style={{ display: "flex", gap: 4 }}>
				{urls.slice(0, 3).map((url) => (
				<div
					key={url}
					style={{
						flex: 1,
						height: 80,
						borderRadius: 6,
						overflow: "hidden",
						background: "var(--ds-surface)",
					}}
				>
					{/* biome-ignore lint/performance/noImgElement: note media thumbnails with dynamic inline styling; next/image not feasible on static export */}
					<img src={url} alt="" style={imgStyle} loading="lazy" />
				</div>
			))}
			</div>
		);
	}

	// 4+ images: 2x2 grid with overflow indicator on the 4th tile.
	return (
		<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
			{urls.slice(0, 4).map((url, index) => {
			const isOverflowTile = index === 3 && count > 4;
			return (
				<div
					key={url}
					style={{
						position: "relative",
							height: 70,
							borderRadius: 6,
							overflow: "hidden",
							background: "var(--ds-surface)",
						}}
					>
					{/* biome-ignore lint/performance/noImgElement: note media thumbnails with dynamic inline styling; next/image not feasible on static export */}
					<img src={url} alt="" style={imgStyle} loading="lazy" />
					{isOverflowTile ? (
							<span
								style={{
									position: "absolute",
									inset: 0,
									display: "grid",
									placeItems: "center",
									background: "rgb(0 0 0 / 0.5)",
									color: "var(--ds-on-media-primary)",
									fontSize: "0.92rem",
									fontWeight: 700,
								}}
							>
								+{count - 4}
							</span>
						) : null}
					</div>
				);
			})}
		</div>
	);
}
